'use client'

import { useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { motion, useReducedMotion } from 'framer-motion'

import { firstNameOf } from '@/lib/name'
import { usePrdIntakeVoice } from './usePrdIntakeVoice'

// WebGL blob — client-only, loaded lazily so the chat never waits on it.
const Orb = dynamic(() => import('@/components/ui/orb/Orb'), { ssr: false })

// ApplyChat.jsx (src/app/(site)/zero-to-hero/apply/ApplyChat.jsx) is the
// architecture this reuses: chat shell, WebsiteAsk-style choice cards, the
// voice-in-bar + Orb modal, the viewport-lock mobile fix (handled by the
// parent page). What's different here: PRD gaps are a fixed, ordered list —
// so instead of accumulating a freeform message log, the transcript is
// DERIVED from `gaps` on every render (answered gaps become Q/A bubble
// pairs, the first unanswered gap becomes the active turn). That makes
// resume-from-reload and the editable summary trivial: there's no client
// state to replay, just gaps.

function MicIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
      <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

function MicOffIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
      <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M4.5 3.5l15 17" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  )
}

// Voice icon in the house dot language: a pixel-dot waveform.
function DotWaveIcon({ className }) {
  const columns = [
    { x: 4, dots: 2 },
    { x: 8, dots: 4 },
    { x: 12, dots: 5 },
    { x: 16, dots: 3 },
    { x: 20, dots: 2 },
  ]
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      {columns.flatMap(({ x, dots }) =>
        Array.from({ length: dots }, (_, i) => {
          const y = 12 + (i - (dots - 1) / 2) * 3.4
          return <circle key={`${x}-${i}`} cx={x} cy={y} r="1.25" fill="currentColor" />
        }),
      )}
    </svg>
  )
}

// A brief radial burst of brand pixels — the completion moment (same house
// language as ApplyChat's SubmittedTakeover).
const BURST_COLORS = ['#C9A84C', '#34d399', '#ffffff', '#d9bd65']

// Exported for reuse by PrdIntakeReview.jsx's client-only "close" affordance
// — same completion flourish, no write behind it there.
export function PixelBurst({ count = 48 }) {
  const [fired, setFired] = useState(false)
  const dotsRef = useRef(null)
  if (!dotsRef.current) {
    dotsRef.current = Array.from({ length: count }, (_, i) => {
      const angle = (i / count) * Math.PI * 2 + (i % 3) * 0.4
      const dist = 60 + (i % 5) * 26
      return {
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist,
        color: BURST_COLORS[i % BURST_COLORS.length],
        delay: (i % 8) * 24,
        size: 3 + (i % 3),
      }
    })
  }
  useEffect(() => {
    const raf = requestAnimationFrame(() => setFired(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <span aria-hidden className="pointer-events-none relative inline-block size-6 motion-reduce:hidden">
      {dotsRef.current.map((d, i) => (
        <span
          key={i}
          style={{
            background: d.color,
            width: d.size,
            height: d.size,
            transform: fired ? `translate(${d.x}px, ${d.y}px) scale(0.4)` : 'translate(0, 0) scale(1)',
            opacity: fired ? 0 : 1,
            transitionDelay: `${d.delay}ms`,
          }}
          className="absolute top-1/2 left-1/2 rounded-full transition-all duration-1000 ease-out"
        />
      ))}
    </span>
  )
}

// k of N progress dots.
function ProgressDots({ total, answeredCount, activeIndex }) {
  if (total <= 0) return null
  return (
    <div
      className="mt-4 flex items-center justify-center gap-1.5"
      aria-label={`Question ${Math.min((activeIndex ?? total) + 1, total)} of ${total}`}
    >
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          aria-hidden
          className={
            i < answeredCount
              ? 'h-1.5 w-4 rounded-full bg-kingdom-gold transition-all'
              : i === activeIndex
                ? 'h-1.5 w-4 rounded-full bg-kingdom-gold/50 transition-all'
                : 'h-1.5 w-1.5 rounded-full bg-white/15 transition-all'
          }
        />
      ))}
    </div>
  )
}

// One gap's answer input: option chips (WebsiteAsk's choice-card pattern) +
// an "Other" free-text fallback, or plain free text when the gap has no
// enumerable options. `onCancel` (summary-edit only) backs out without
// saving; `voiceButton` slots the orb trigger into the free-text row.
// Exported for reuse by PrdIntakeReview.jsx (founder self-serve edit/
// resubmit screen) — same editable-answer interaction, no chat scaffolding
// around it.
export function GapAnswerCard({ gap, busy, initialValue, onSubmit, onCancel, voiceButton }) {
  const hasOptions = Array.isArray(gap.options) && gap.options.length > 0
  const [mode, setMode] = useState(hasOptions ? 'choice' : 'text')
  const [text, setText] = useState(initialValue || '')

  function submitText(e) {
    e?.preventDefault()
    const v = text.trim()
    if (!v) return
    onSubmit(v)
  }

  return (
    <div className="max-w-[92%] rounded-2xl border border-kingdom-gold/30 bg-kingdom-gold/[0.05] p-4">
      {gap.whyItMatters && (
        <p className="mb-3 text-xs leading-relaxed text-neutral-400 italic">{gap.whyItMatters}</p>
      )}
      {mode === 'choice' ? (
        <div className="flex flex-wrap items-center gap-2">
          {gap.options.map((opt) => (
            <button
              key={opt}
              type="button"
              disabled={busy}
              onClick={() => onSubmit(opt)}
              className="rounded-full border border-kingdom-gold/40 bg-neutral-950 px-4 py-2 text-sm font-medium text-kingdom-gold transition hover:border-kingdom-gold disabled:opacity-50"
            >
              {opt}
            </button>
          ))}
          <button
            type="button"
            disabled={busy}
            onClick={() => setMode('text')}
            className="rounded-full border border-white/15 bg-neutral-950 px-4 py-2 text-sm font-medium text-neutral-300 transition hover:border-white/40 disabled:opacity-50"
          >
            Other
          </button>
          {voiceButton}
          {onCancel && (
            <button
              type="button"
              disabled={busy}
              onClick={onCancel}
              className="rounded-full px-3 py-2 text-sm text-neutral-500 transition hover:text-neutral-300"
            >
              Cancel
            </button>
          )}
        </div>
      ) : (
        <form onSubmit={submitText} className="space-y-2">
          <textarea
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={2000}
            rows={2}
            disabled={busy}
            placeholder="Type your answer…"
            className="w-full resize-none rounded-xl border border-white/15 bg-neutral-950 px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:border-kingdom-gold/60 focus:outline-none disabled:opacity-50"
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="submit"
              disabled={busy || !text.trim()}
              className="rounded-full border border-kingdom-gold/40 bg-kingdom-gold px-4 py-2 text-sm font-semibold text-neutral-950 transition hover:bg-[#d9bd65] disabled:opacity-50"
            >
              Answer
            </button>
            {hasOptions && (
              <button
                type="button"
                disabled={busy}
                onClick={() => setMode('choice')}
                className="rounded-full px-3 py-2 text-sm text-neutral-500 transition hover:text-neutral-300"
              >
                Back to suggestions
              </button>
            )}
            {voiceButton}
            {onCancel && (
              <button
                type="button"
                disabled={busy}
                onClick={onCancel}
                className="rounded-full px-3 py-2 text-sm text-neutral-500 transition hover:text-neutral-300"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  )
}

// Quality gate panel (intake reset Task 3) — renders when a confirm/
// resubmit attempt 422s with `qualityFlags`. Advisory only: the founder can
// either revise the flagged answer (`onEdit`, jumps back into that gap's
// GapAnswerCard) or explicitly mark it "use my answer as-is" (`onToggleOverride`)
// and retry (`onRetry`, which re-sends confirm with every currently-toggled
// gapIndex in `overrides`) — overriding every flag always completes.
// Exported for PrdIntakeReview.jsx's resubmit path (T2's review screen hits
// the same server-side gate, so it gets the same UI).
//
// NEVER render answer content here — a flag only ever carries
// gapIndex/question (agent-authored, safe to show)/reason.
export function QualityFlagsPanel({ flags, overrides, busy, onToggleOverride, onEdit, onRetry, retryLabel }) {
  const shouldReduceMotion = useReducedMotion()
  return (
    <motion.div
      initial={shouldReduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="w-full max-w-[95%] rounded-2xl border border-kingdom-gold/25 bg-kingdom-gold/[0.04] p-5"
    >
      <p className="text-sm leading-relaxed text-neutral-200">
        A couple of these could use a bit more before we build from them — take another
        pass, or tell us they&apos;re fine as-is.
      </p>
      <div className="mt-4 space-y-3">
        {flags.map((flag) => {
          const overridden = overrides.has(flag.gapIndex)
          return (
            <div
              key={flag.gapIndex}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
            >
              <p className="text-sm leading-relaxed text-neutral-100">{flag.question}</p>
              <p className="mt-1 text-xs leading-relaxed text-neutral-400 italic">
                A sentence or two here helps us build this right.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onEdit(flag.gapIndex)}
                  className="flex min-h-11 items-center rounded-full border border-kingdom-gold/40 bg-neutral-950 px-4 py-2 text-sm font-medium text-kingdom-gold transition hover:border-kingdom-gold disabled:opacity-50"
                >
                  Say more
                </button>
                <button
                  type="button"
                  disabled={busy}
                  aria-pressed={overridden}
                  onClick={() => onToggleOverride(flag.gapIndex)}
                  className={
                    overridden
                      ? 'flex min-h-11 items-center rounded-full border border-emerald-400/50 bg-emerald-400/10 px-4 py-2 text-sm font-medium text-emerald-200 transition'
                      : 'flex min-h-11 items-center rounded-full border border-white/15 bg-neutral-950 px-4 py-2 text-sm font-medium text-neutral-300 transition hover:border-white/40'
                  }
                >
                  {overridden ? "Using as-is ✓" : 'Use my answer as-is'}
                </button>
              </div>
            </div>
          )
        })}
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={onRetry}
        className="mt-5 w-full rounded-full bg-kingdom-gold px-6 py-3.5 font-semibold text-neutral-950 transition hover:bg-[#d9bd65] disabled:opacity-60"
      >
        {busy ? 'Submitting…' : retryLabel || 'Confirm and submit'}
      </button>
    </motion.div>
  )
}

export function PrdIntakeChat({ token, founderName, initialGaps }) {
  const first = firstNameOf(founderName)
  const [gaps, setGaps] = useState(Array.isArray(initialGaps) ? initialGaps : [])
  const [editingIndex, setEditingIndex] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [confirmed, setConfirmed] = useState(false)
  // Quality gate state (T3): set from a 422's `qualityFlags` body; `overrides`
  // tracks which flagged gapIndexes the founder has toggled "use as-is" for
  // this attempt. Any successful answer edit invalidates both — the next
  // confirm re-runs the gate fresh against whatever's currently answered.
  const [qualityFlags, setQualityFlags] = useState(null)
  const [overrides, setOverrides] = useState(() => new Set())
  const endRef = useRef(null)

  const total = gaps.length
  const answeredCount = gaps.filter((g) => g.answer).length
  const firstUnanswered = gaps.findIndex((g) => !g.answer)
  const allAnswered = firstUnanswered === -1
  const activeIndex = editingIndex ?? (allAnswered ? null : firstUnanswered)

  function clearQualityGate() {
    setQualityFlags(null)
    setOverrides(new Set())
  }

  function toggleOverride(index) {
    setOverrides((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  const voice = usePrdIntakeVoice({
    token,
    founderName,
    onAnswer: ({ gapIndex, answer }) => {
      if (gapIndex == null || !answer) return
      setGaps((g) => {
        const copy = [...g]
        copy[gapIndex] = { ...copy[gapIndex], answer }
        return copy
      })
      setEditingIndex(null)
      setError(null)
      clearQualityGate()
    },
    onError: (msg) => setError(msg),
  })
  const voiceModalOpen = voice.callState !== 'idle'

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [gaps, editingIndex, busy])

  // questionText rides along on every write so the server can pin the
  // atomic UPDATE to the exact question this tab is looking at, not just its
  // numeric index — see src/lib/prd/intake.ts's writeGapAnswer identity
  // guard. `data.reload` (Critical #1a review fix) means an admin
  // regenerated this PRD's gaps while this tab sat open and the answer was
  // NOT written — the graceful recovery is a full reload, which re-runs the
  // page's server component and hands PrdIntakeChat a fresh `initialGaps`
  // so the founder resumes on the current question set instead of being
  // stuck on a stale, silently-broken one.
  async function answerGap(index, answerText, { revise = false } = {}) {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/z2h/prd/intake/turn', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          gapIndex: index,
          questionText: gaps[index]?.question || '',
          answer: answerText,
          ...(revise ? { action: 'revise' } : {}),
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        if (data.reload) {
          window.location.reload()
          return
        }
        throw new Error(data.error || 'Something went wrong')
      }
      setGaps((g) => {
        const copy = [...g]
        copy[index] = { ...copy[index], answer: answerText }
        return copy
      })
      setEditingIndex(null)
      clearQualityGate()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  // Quality gate (T3): a 422 with `qualityFlags` means one or more answers
  // read as empty/dismissive/too-short — stop here (no completion happened
  // server-side) and show the gentle nudge panel instead of throwing. Retrying
  // sends whatever gapIndexes are currently toggled "use as-is" in `overrideIndexes`.
  async function confirm(overrideIndexes = []) {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/z2h/prd/intake/turn', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          action: 'confirm',
          ...(overrideIndexes.length ? { overrides: overrideIndexes } : {}),
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.status === 422 && Array.isArray(data.qualityFlags)) {
        setQualityFlags(data.qualityFlags)
        return
      }
      if (!res.ok || !data.ok) {
        if (data.reload) {
          window.location.reload()
          return
        }
        throw new Error(data.error || 'Something went wrong')
      }
      setQualityFlags(null)
      setConfirmed(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (confirmed) {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        <div className="mx-auto mt-10 w-full max-w-md">
          <div className="rounded-3xl border border-kingdom-gold/30 bg-kingdom-gold/[0.06] p-8 text-center">
            <div className="flex justify-center pt-4 pb-8">
              <PixelBurst />
            </div>
            <h2 className="font-display text-2xl font-medium">Thank you{first ? `, ${first}` : ''}.</h2>
            <p className="mt-3 text-sm leading-6 text-white/70">
              Your answers are with the team — the requirements doc is being finalized for
              internal review. You&apos;ll hear from us about next steps.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const activeGap = activeIndex != null ? gaps[activeIndex] : null

  return (
    <div className="mt-6 flex min-h-0 flex-1 flex-col">
      <ProgressDots total={total} answeredCount={answeredCount} activeIndex={activeIndex} />

      {/* Transcript scrolls internally, derived straight from `gaps` — no
          replayable message log, so a reload always renders exactly where
          the founder left off. */}
      <div className="mt-4 min-h-0 flex-1 space-y-4 overflow-y-auto rounded-3xl border border-white/10 bg-white/[0.03] p-5 sm:p-7">
        <div className="flex justify-start">
          <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-white/8 px-4 py-3 text-sm leading-relaxed text-neutral-100">
            Welcome back{first ? `, ${first}` : ''}. We&apos;ve drafted your product
            requirements from your application and our conversations — we just need {total}{' '}
            thing{total === 1 ? '' : 's'} confirmed.
          </div>
        </div>

        {gaps.map((gap, i) => {
          if (!gap.answer || editingIndex === i) return null
          return (
            <div key={i}>
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-white/8 px-4 py-3 text-sm leading-relaxed text-neutral-100">
                  {gap.question}
                </div>
              </div>
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setEditingIndex(i)}
                  title="Tap to change your answer"
                  className="max-w-[85%] rounded-2xl rounded-br-sm bg-kingdom-gold/90 px-4 py-3 text-left text-sm leading-relaxed text-neutral-950 transition hover:bg-kingdom-gold disabled:opacity-60"
                >
                  {gap.answer}
                </button>
              </div>
            </div>
          )
        })}

        {activeGap && (
          <div>
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-white/8 px-4 py-3 text-sm leading-relaxed text-neutral-100">
                {activeGap.question}
              </div>
            </div>
            <div className="mt-2 flex justify-start">
              <GapAnswerCard
                gap={activeGap}
                busy={busy}
                initialValue={activeGap.answer || ''}
                onCancel={editingIndex != null ? () => setEditingIndex(null) : null}
                onSubmit={(val) => answerGap(activeIndex, val, { revise: editingIndex != null })}
                voiceButton={
                  voice.available ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        voice.start({
                          gapIndex: activeIndex,
                          gap: activeGap,
                          gapNumber: activeIndex + 1,
                          totalGaps: total,
                        })
                      }
                      aria-label="Answer by voice"
                      title="Answer by voice"
                      className="group relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-kingdom-gold/40 bg-neutral-950 transition hover:border-kingdom-gold disabled:opacity-40"
                    >
                      <span className="pointer-events-none absolute inset-0">
                        <Orb hue={45} hoverIntensity={1.6} rotateOnHover backgroundColor="#0a0a0a" />
                      </span>
                      <DotWaveIcon className="pointer-events-none relative z-10 size-4 text-white drop-shadow-[0_0_6px_rgba(0,0,0,0.9)] transition-colors group-hover:text-kingdom-gold" />
                    </button>
                  ) : null
                }
              />
            </div>
          </div>
        )}

        {allAnswered && editingIndex === null && qualityFlags && qualityFlags.length > 0 && (
          <div className="flex justify-start">
            <QualityFlagsPanel
              flags={qualityFlags}
              overrides={overrides}
              busy={busy}
              onToggleOverride={toggleOverride}
              onEdit={(idx) => {
                setQualityFlags(null)
                setEditingIndex(idx)
              }}
              onRetry={() => confirm([...overrides])}
            />
          </div>
        )}

        {allAnswered && editingIndex === null && !(qualityFlags && qualityFlags.length > 0) && (
          <div className="flex justify-start">
            <div className="w-full max-w-[95%]">
              <p className="mb-3 text-sm leading-relaxed text-neutral-300">
                Here&apos;s everything — tap any answer above to change it, then confirm
                when it looks right.
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={() => confirm()}
                className="w-full rounded-full bg-kingdom-gold px-6 py-3.5 font-semibold text-neutral-950 transition hover:bg-[#d9bd65] disabled:opacity-60"
              >
                {busy ? 'Submitting…' : 'Confirm and submit'}
              </button>
            </div>
          </div>
        )}

        <div ref={endRef} />
      </div>

      {error && <p className="mt-3 text-center text-sm text-red-400">{error}</p>}

      {/* Voice mode — the same AI-orb modal treatment as ApplyChat/ScreenChat.
          Scoped to the single active gap; closing it any time returns to the
          text card, which still holds whatever was captured before it closed. */}
      {voiceModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/80 backdrop-blur-md"
          onClick={voice.callState === 'finalizing' ? undefined : voice.stop}
        >
          <div
            className="relative mx-4 flex w-full max-w-sm flex-col items-center rounded-3xl border border-white/10 bg-neutral-900/90 px-8 pt-10 pb-8 text-center shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
            onClick={(e) => e.stopPropagation()}
          >
            {voice.callState !== 'finalizing' && (
              <button
                type="button"
                onClick={voice.stop}
                aria-label="End voice answer"
                className="absolute top-4 right-4 flex size-9 items-center justify-center rounded-full border border-white/15 text-sm text-neutral-400 transition hover:border-red-400/50 hover:text-red-300"
              >
                ✕
              </button>
            )}

            <div className="relative size-48 sm:size-56">
              <Orb
                hue={45}
                hoverIntensity={2.4}
                rotateOnHover
                forceHoverState={voice.assistantSpeaking}
                backgroundColor="#171717"
              />
            </div>

            <p className="mt-6 font-display text-lg text-white">
              {voice.callState === 'connecting'
                ? 'Connecting…'
                : voice.callState === 'finalizing'
                  ? 'Saving your answer…'
                  : voice.assistantSpeaking
                    ? 'Speaking…'
                    : voice.muted
                      ? 'Muted — tap the mic to speak'
                      : 'Listening — go ahead'}
            </p>

            <p className="mt-3 min-h-10 text-sm leading-relaxed text-neutral-400 italic">
              {voice.callState !== 'finalizing' && voice.liveCaption?.text
                ? `${voice.liveCaption.text}…`
                : ''}
            </p>

            <button
              type="button"
              onClick={voice.toggleMute}
              aria-label={voice.muted ? 'Unmute microphone' : 'Mute microphone'}
              aria-pressed={voice.muted}
              title={voice.muted ? 'Unmute' : 'Mute'}
              className={
                voice.muted
                  ? 'mt-4 flex size-12 items-center justify-center rounded-full bg-red-500/90 text-white transition hover:bg-red-500'
                  : 'mt-4 flex size-12 items-center justify-center rounded-full border border-white/15 text-neutral-300 transition hover:border-kingdom-gold hover:text-kingdom-gold'
              }
            >
              {voice.muted ? <MicOffIcon className="size-5" /> : <MicIcon className="size-5" />}
            </button>

            <p className="mt-4 text-[0.68rem] text-neutral-500">
              Close this any time to answer by text instead.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
